<template>
  <UModal v-model:open="isOpen" :ui="{ content: 'max-w-3xl w-full' }">
    <template #content>
      <div class="relative">
        <button
          type="button"
          class="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-elevated/80 text-muted ring-1 ring-default backdrop-blur-sm transition-colors hover:text-highlighted"
          aria-label="Închide"
          @click="close"
        >
          <UIcon name="i-heroicons-x-mark" class="h-4 w-4" />
        </button>
      <div v-if="displayProduct" class="grid grid-cols-1 gap-6 p-5 sm:grid-cols-2 sm:p-6">
        <!-- Gallery -->
        <div>
          <div ref="quickViewImageRef" class="aspect-[4/5] overflow-hidden rounded-xl bg-muted">
            <ProductImageZoom :enabled="storefrontImageZoom">
              <AppImage
                :key="activeImage?.id ?? 0"
                :src="activeImage?.url"
                :alt="displayProduct.name"
                preset="product"
                fit="inside"
                width="600"
                height="750"
                sizes="xs:90vw sm:400px md:400px"
                class="h-full w-full"
                loading="eager"
              />
            </ProductImageZoom>
          </div>
          <div v-if="(displayProduct.images?.length ?? 0) > 1" class="mt-3 flex gap-2 overflow-x-auto">
            <button
              v-for="(img, idx) in displayProduct.images"
              :key="img.id"
              type="button"
              class="product-gallery-thumb h-16 w-12 shrink-0 bg-muted"
              :class="{ active: activeImageIndex === idx }"
              :aria-label="`Imagine ${idx + 1}`"
              @click="activeImageIndex = idx"
            >
              <AppImage
                :src="img.url"
                alt=""
                preset="thumbnail"
                fit="inside"
                width="48"
                height="64"
                sizes="48px"
                loading="lazy"
                class="h-full w-full"
              />
            </button>
          </div>
        </div>

        <!-- Info -->
        <div class="flex flex-col">
          <p v-if="displayProduct.brand" class="text-xs uppercase tracking-wide text-muted">
            {{ displayProduct.brand.name }}
          </p>
          <h2 class="mt-1 text-lg font-bold leading-snug text-highlighted">{{ displayProduct.name }}</h2>

          <div v-if="reviewsEnabled && displayProduct.ratingCount > 0" class="mt-2 flex items-center gap-1.5">
            <div class="flex">
              <UIcon
                v-for="i in 5"
                :key="i"
                :name="i <= Math.round(displayProduct.ratingAvg) ? 'i-heroicons-star-solid' : 'i-heroicons-star'"
                class="h-4 w-4"
                :class="i <= Math.round(displayProduct.ratingAvg) ? 'text-yellow-400' : 'text-dimmed'"
              />
            </div>
            <span class="text-xs text-muted">({{ displayProduct.ratingCount }})</span>
          </div>

          <div class="mt-4">
            <p v-if="hasDiscount" class="text-sm text-dimmed line-through">{{ formatPrice(displayProduct.price) }}</p>
            <p
              class="text-2xl font-bold"
              :class="hasDiscount ? 'text-rose-600 dark:text-rose-400' : 'text-highlighted'"
            >
              {{ formatPriceWithUnit(displayPrice, displayProduct.priceUnit) }}
            </p>
            <TbiInstalmentHint compact :amount="displayPrice" />
            <p class="text-xs text-muted">TVA inclus</p>
          </div>

          <p v-if="displayProduct.shortDescription" class="mt-3 line-clamp-3 text-sm text-toned">
            {{ displayProduct.shortDescription }}
          </p>

          <UBadge
            v-if="stockManagementEnabled && !shopServicesEnabled"
            class="mt-3 w-fit"
            :color="displayProduct.stock > 0 ? 'success' : 'error'"
            variant="subtle"
          >
            {{ displayProduct.stock > 0 ? `În stoc (${displayProduct.stock})` : 'Stoc epuizat' }}
          </UBadge>

          <div class="mt-auto pt-5 space-y-2">
            <div class="flex items-center gap-2">
              <UInputNumber
                v-if="!productHasPriceUnit(displayProduct)"
                v-model="quantity"
                :min="1"
                :step="1"
                :max="stockManagementEnabled && !shopServicesEnabled ? Math.max(1, displayProduct.stock) : undefined"
                class="w-24"
              />
              <UButton
                block
                class="flex-1"
                color="primary"
                :icon="needsConfiguration ? 'i-heroicons-adjustments-horizontal' : 'i-heroicons-shopping-cart'"
                :label="configureCtaLabel
                  || (stockManagementEnabled && !shopServicesEnabled && displayProduct.stock <= 0 ? 'Indisponibil' : 'Adaugă în coș')"
                :disabled="!needsConfiguration && stockManagementEnabled && !shopServicesEnabled && displayProduct.stock <= 0"
                @click="handleAdd"
              />
            </div>
            <UButton
              block
              variant="ghost"
              :to="shopPath(`/produse/${displayProduct.slug}`)"
              label="Vezi toate detaliile"
              trailing-icon="i-heroicons-arrow-right"
              @click="close"
            />
          </div>
        </div>
      </div>

      <div v-else class="flex items-center justify-center gap-2 p-16 text-muted">
        <UIcon name="i-heroicons-arrow-path" class="h-5 w-5 animate-spin" />
        Se încarcă...
      </div>
      </div>
    </template>
  </UModal>
</template>

<script setup lang="ts">
import type { ApiResponse, Product } from '~/types'
import { productIsComplex, productIsPackage } from '~/utils/productPackage'
import { productIsVariable } from '~/utils/productVariants'
import { formatPriceWithUnit } from '~/utils/productCard'
import { productHasPriceUnit } from '~/utils/shopServices'

const shopPath = useShopPath()
const { activeProduct, isOpen, close } = useQuickView()
const { addToCart } = useCart()
const { formatPrice } = useSeo()
const config = useRuntimeConfig()
const { stockManagementEnabled, shopServicesEnabled, reviewsEnabled, storefrontImageZoom } = useSiteSettings()
const quickViewImageRef = ref<HTMLElement | null>(null)

const detailed = ref<Product | null>(null)
const activeImageIndex = ref(0)
const quantity = ref(1)

const displayProduct = computed(() => detailed.value ?? activeProduct.value)

const activeImage = computed(() => {
  const images = displayProduct.value?.images ?? []
  return images[activeImageIndex.value] ?? images.find(i => i.isPrimary) ?? images[0]
})

const hasDiscount = computed(() => {
  const p = displayProduct.value
  return !!p?.salePrice && p.salePrice < p.price
})

const displayPrice = computed(() => {
  const p = displayProduct.value
  return p?.salePrice ?? p?.price ?? 0
})

const isPackageProduct = computed(() => productIsPackage(displayProduct.value))
const needsConfiguration = computed(() =>
  isPackageProduct.value
  || productIsComplex(displayProduct.value)
  || productIsVariable(displayProduct.value)
  || productHasPriceUnit(displayProduct.value),
)
const configureCtaLabel = computed(() => {
  if (!needsConfiguration.value) return null
  if (isPackageProduct.value) return 'Configurează pachetul'
  if (productIsVariable(displayProduct.value)) return 'Alege opțiunile'
  if (productHasPriceUnit(displayProduct.value)) return 'Alege cantitatea'
  return 'Configurează'
})

watch(isOpen, async (open) => {
  if (!open) return
  activeImageIndex.value = 0
  quantity.value = 1
  detailed.value = null

  const slug = activeProduct.value?.slug
  if (!slug) return

  try {
    const response = await $fetch<ApiResponse<Product>>(
      `${useShopApiBase().value}/products/${slug}`,
    )
    if (response.success && response.data) {
      detailed.value = response.data
    }
  } catch {
    // fall back to the card's product data
  }
})

function handleAdd() {
  const p = displayProduct.value
  if (!p) return
  if (needsConfiguration.value) {
    close()
    navigateTo(shopPath(`/produse/${p.slug}`))
    return
  }
  if (stockManagementEnabled.value && !shopServicesEnabled.value && p.stock <= 0) return
  addToCart(p, quantity.value, undefined, { openDrawer: false, flyFrom: quickViewImageRef.value })
}
</script>
